import { FC, useMemo } from 'react'
import {
  Text,
  Button,
  GreyGrey20,
  GreyGrey30,
  GreyGrey60,
  GreyGrey80,
  GreyGrey100,
  Primary12Regular,
  Primary12SemiBold,
  Primary13SemiBold,
  Primary14SemiBold,
  TextOverflow,
  Modal,
  useModalClose,
  Box,
  Flex
} from '@sequencehq/core-components'
import { formatBytes } from './performanceMonitorBar.constants'
import { QueryCacheEntry } from './performanceMonitorBar.types'

type CacheEntriesModalProps = {
  title: string
  entries: QueryCacheEntry[]
  onClose: () => void
}

export const CacheEntriesModal: FC<CacheEntriesModalProps> = ({
  title,
  entries,
  onClose
}) => {
  const sortedEntries = useMemo(
    () => [...entries].sort((a, b) => b.bytes - a.bytes),
    [entries]
  )

  const totalBytes = sortedEntries.reduce((acc, entry) => acc + entry.bytes, 0)

  return (
    <Modal
      isOpen
      onClose={onClose}
      width="576px"
      top="80px"
      data-testid="performance-monitor-bar.cacheEntriesModal"
    >
      <Modal.Header showCloseButton withBorder={false}>
        <Text as="span" color={GreyGrey100} {...Primary14SemiBold}>
          {title}
        </Text>
      </Modal.Header>
      <Modal.Content padding={4} minHeight="auto">
        <Text {...Primary12Regular} color={GreyGrey60} marginBottom="12px">
          {sortedEntries.length} entries · {formatBytes(totalBytes)} total
        </Text>
        <Box
          border={`1px solid ${GreyGrey30}`}
          borderRadius="6px"
          maxHeight="60vh"
          overflowY="auto"
        >
          <Flex
            backgroundColor={GreyGrey20}
            paddingX="12px"
            paddingY="8px"
            gap="12px"
            position="sticky"
            top={0}
            zIndex={1}
          >
            <Text {...Primary12SemiBold} color={GreyGrey80} flex="1">
              Query key
            </Text>
            <Text
              {...Primary12SemiBold}
              color={GreyGrey80}
              width="80px"
              textAlign="right"
            >
              Size
            </Text>
          </Flex>
          {sortedEntries.length === 0 ? (
            <Box paddingX="12px" paddingY="16px">
              <Text {...Primary12Regular} color={GreyGrey60}>
                No cached entries with data.
              </Text>
            </Box>
          ) : (
            sortedEntries.map(entry => (
              <Flex
                key={entry.key}
                paddingX="12px"
                paddingY="6px"
                gap="12px"
                borderTop={`1px solid ${GreyGrey30}`}
                alignItems="center"
              >
                <Box
                  flex="1"
                  minWidth={0}
                  {...Primary12Regular}
                  color={GreyGrey80}
                  fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                >
                  <TextOverflow>{entry.key}</TextOverflow>
                </Box>
                <Text
                  {...Primary12SemiBold}
                  color={GreyGrey80}
                  width="80px"
                  textAlign="right"
                >
                  {formatBytes(entry.bytes)}
                </Text>
              </Flex>
            ))
          )}
        </Box>
      </Modal.Content>
      <Modal.Footer withBorder={false}>
        <ModalCloseButton />
      </Modal.Footer>
    </Modal>
  )
}

const ModalCloseButton: FC = () => {
  const requestClose = useModalClose()

  return (
    <Button
      data-testid="performance-monitor-bar.cacheEntriesModal.close"
      variant="secondary"
      onClick={requestClose}
      {...Primary13SemiBold}
    >
      Close
    </Button>
  )
}
